import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const NAVY  = '#071B3A';
const GOLD  = '#C99316';
const IVORY = '#FAF8F3';

// Shows the most recently published featured article from the CMS
const FeaturedArticleBlock = () => {
  const [article, setArticle] = useState(null);

  useEffect(() => {
    // Fetch the pinned flagship article by known slug
    fetch(`${API_URL}/api/public/articles/beyond-edi-becoming-culturally-fluent`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data && data.status === 'published') setArticle(data);
      })
      .catch(() => {});
  }, []);

  if (!article) return null;

  return (
    <section
      className="py-16 md:py-20"
      style={{ backgroundColor: '#EDE8DC' }}
      data-testid="featured-article-block"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-24">

        {/* Section label */}
        <div className="flex items-center justify-between mb-10">
          <div>
            <span
              className="block text-[11px] tracking-[0.3em] uppercase font-sans font-medium mb-1"
              style={{ color: GOLD + 'B3' }}
            >
              Featured Insight
            </span>
            <div style={{ width: '28px', height: '1px', backgroundColor: GOLD, opacity: 0.35 }} />
          </div>
          <Link
            to="/articles"
            className="hidden sm:flex items-center gap-1.5 font-sans text-[11px] uppercase tracking-[0.18em] transition-all duration-300 hover:gap-2.5"
            style={{ color: 'rgba(7,27,58,0.4)' }}
          >
            All Articles <ArrowRight size={12} />
          </Link>
        </div>

        {/* Editorial card */}
        <Link
          to={`/articles/${article.slug}`}
          className="group block"
          data-testid="featured-article-link"
        >
          <div
            className="grid lg:grid-cols-[5fr_4fr] gap-0 overflow-hidden transition-all duration-500"
            style={{ border: '1px solid rgba(7,27,58,0.08)' }}
          >
            {/* Thumbnail — left */}
            {article.featured_image && (
              <div
                className="relative overflow-hidden"
                style={{ minHeight: '280px', maxHeight: '420px' }}
              >
                <img
                  src={article.featured_image}
                  alt={article.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
                  style={{ display: 'block' }}
                />
                {/* Subtle navy overlay on hover */}
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{ backgroundColor: 'rgba(7,27,58,0.08)' }}
                />
              </div>
            )}

            {/* Content — right */}
            <div
              className="p-8 md:p-10 lg:p-12 flex flex-col justify-between"
              style={{ backgroundColor: 'white' }}
            >
              <div>
                {/* Categories */}
                <div className="flex flex-wrap items-center gap-2.5 mb-6">
                  {article.categories?.slice(0, 2).map((cat) => (
                    <span
                      key={cat}
                      className="font-sans text-[9px] uppercase tracking-[0.22em] px-2.5 py-1"
                      style={{ backgroundColor: 'rgba(201,147,22,0.08)', color: GOLD }}
                    >
                      {cat}
                    </span>
                  ))}
                  {article.reading_time && (
                    <span
                      className="flex items-center gap-1 font-sans text-[10px]"
                      style={{ color: 'rgba(7,27,58,0.3)' }}
                    >
                      <Clock size={10} /> {article.reading_time} min read
                    </span>
                  )}
                </div>

                {/* Title */}
                <h2
                  className="font-serif font-light tracking-tight leading-[1.12] mb-4 transition-colors duration-300"
                  style={{
                    fontSize: 'clamp(1.55rem, 2.8vw, 2.2rem)',
                    color: NAVY,
                  }}
                >
                  {article.title}
                </h2>

                {/* Excerpt */}
                {article.excerpt && (
                  <p
                    className="font-sans font-light leading-[1.8] mb-6"
                    style={{ fontSize: '0.9rem', color: 'rgba(7,27,58,0.56)' }}
                  >
                    {article.excerpt}
                  </p>
                )}
              </div>

              {/* Footer: author + CTA */}
              <div>
                <div
                  className="mb-5 pt-5"
                  style={{ borderTop: '1px solid rgba(7,27,58,0.07)' }}
                >
                  <p
                    className="font-sans text-sm font-medium"
                    style={{ color: 'rgba(7,27,58,0.6)' }}
                  >
                    {article.author}
                  </p>
                  <p
                    className="font-sans text-[11px] mt-0.5"
                    style={{ color: 'rgba(7,27,58,0.32)' }}
                  >
                    Paediatrician · Author · Founder, Third Culture Leadership
                  </p>
                </div>

                <span
                  className="inline-flex items-center gap-2 font-sans text-[11px] uppercase tracking-[0.2em] font-medium transition-all duration-300 group-hover:gap-3"
                  style={{ color: GOLD }}
                  data-testid="featured-article-read-btn"
                >
                  Read Article <ArrowRight size={13} />
                </span>
              </div>
            </div>
          </div>
        </Link>

        {/* Mobile: All Articles link */}
        <div className="flex justify-center mt-6 sm:hidden">
          <Link
            to="/articles"
            className="flex items-center gap-1.5 font-sans text-[11px] uppercase tracking-[0.18em]"
            style={{ color: 'rgba(7,27,58,0.4)' }}
          >
            All Articles <ArrowRight size={12} />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default FeaturedArticleBlock;
